import { OfferFragment } from '@occc/graphql';
import { constraintHelper } from '@occc-app/offerHelpers/utils/constraintHelper';
import { DirtyFieldsType, Operation } from '@occc-app/offerHelpers/types';
import { isFieldDirty } from '@occc-app/offerHelpers/utils';
import { makeDeleteOperation } from '@occc-app/offerHelpers/operations/makeDeleteOperation';
import { makeCreateSubBrandConstraintOperation } from '@occc-app/offerHelpers/operations/constraints';
import { useConfig } from '@occc/config';

import { SubBrandConstraintFormValues, SubBrand } from './sub-brand-constraint.types';

export const defaultSubBrandItem: SubBrand = {
  uuid: '',
  name: 'Universal',
};

export const mapSubBrandsQueryResponseToRawArray = (response: SubBrand[]): SubBrand[] => [
  defaultSubBrandItem,
  ...response.map(({ uuid, name }) => ({ uuid, name })),
];

export const mapRawSubBrandsResponseItemToAutocompleteOption = (item: SubBrand) => ({
  value: item.uuid,
  label: item.name,
  _raw: item,
});

export const getSubBrandConstraint = (offer: OfferFragment | null | undefined) => {
  return offer?.constraints.find(constraintHelper.isSubBrand);
};

export const getSubBrandConstraintInitialValues = (
  offer: OfferFragment | null | undefined,
  subBrandsList?: SubBrand[],
): SubBrandConstraintFormValues => {
  const subBrandConstraint = getSubBrandConstraint(offer);
  const filteredSubBrand = subBrandsList?.find(
    (item) => item.uuid === subBrandConstraint?.subBrandUuids?.[0],
  );

  if (subBrandConstraint) {
    return {
      subBrandConstraint: {
        id: subBrandConstraint.id,
        subBrand: {
          uuid: subBrandConstraint.subBrandUuids?.length ? subBrandConstraint.subBrandUuids[0] : '',
          name: filteredSubBrand?.name || '',
        },
      },
    };
  }

  return {
    subBrandConstraint: {
      subBrand: defaultSubBrandItem,
    },
  };
};

export const getSubBrandConstraintOperations = (
  offerId: string,
  offer: OfferFragment | undefined,
  values: SubBrandConstraintFormValues,
  dirtyFields: DirtyFieldsType,
): Operation[] => {
  const operations: Operation[] = [];
  const constraintId = values.subBrandConstraint?.id ?? getSubBrandConstraint(offer)?.id;

  if (isFieldDirty<SubBrandConstraintFormValues>(dirtyFields, 'subBrandConstraint.subBrand')) {
    if (constraintId) {
      operations.push(makeDeleteOperation('constraintDelete', constraintId));
    }

    if (values.subBrandConstraint?.subBrand?.uuid) {
      operations.push(
        makeCreateSubBrandConstraintOperation(offerId, [values.subBrandConstraint.subBrand.uuid]),
      );
    }
  }

  return operations;
};

export const useSubBrandsQuery = () => {
  const { config } = useConfig();

  return {
    executeQuery: () => null,
    response: config.subBrands?.list || [],
  };
};
